import type { PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';
import type { PositionValue } from '@elfsight/configurator-contracts';
import { useWindowKeydown } from '@elfsight/lib-utils';
import { FloatingPanel, MobileBottomSheet } from '@elfsight/widget-modal';
import { useIsMobile } from '@elfsight/widget-utils';
import { useLingui } from '@lingui/react/macro';
import { useSettings } from '@wu/widget-core';
import { defaultSettings } from '../../defaults';

const FLOATING_BUTTON_SIZE = 40;
const OFFSET = 16;

type WindowProps = PropsWithChildren<{
  open: boolean;
  id: string;
  onCloseRequest: () => void;
}>;

export function Window({ children, open, id, onCloseRequest }: WindowProps) {
  const { t } = useLingui();
  const { position, renderThumbnailMode } = useSettings();
  const isMobile = useIsMobile();

  const verticalOffset = renderThumbnailMode
    ? defaultSettings.position.verticalOffset + FLOATING_BUTTON_SIZE + OFFSET
    : OFFSET;

  const ariaLabel = t`Accessibility Widget Panel`;

  useWindowKeydown('Escape', onCloseRequest);

  return isMobile ? (
    <MobileBottomSheet
      id={id}
      aria-label={ariaLabel}
      open={open}
      closeOnBackdrop={false}
      backdropBackgroundColor="transparent" // hide backdrop
      onCloseRequest={onCloseRequest}
    >
      {children}
    </MobileBottomSheet>
  ) : (
    <StyledFloatingPanel
      $renderThumbnailMode={renderThumbnailMode}
      id={id}
      aria-label={ariaLabel}
      position={`bottom-${position.position}` as PositionValue}
      verticalOffset={verticalOffset}
      horizontalOffset={OFFSET}
      hideShadow={renderThumbnailMode}
      height="auto"
      open={open}
      onCloseRequest={onCloseRequest}
    >
      {children}
    </StyledFloatingPanel>
  );
}

const StyledFloatingPanel = styled(FloatingPanel)<{
  $renderThumbnailMode?: boolean;
}>`
  ${({ $renderThumbnailMode }) => css`
    display: flex;

    ${$renderThumbnailMode &&
    css`
      box-shadow: 0px 6px 30px 0px rgba(0, 0, 0, 0.15);
      transition: none;
    `}
  `}
`;
